
import { Component, Vue, namespace, Watch } from 'nuxt-property-decorator';
import {
  GetFormEntriesModel,
  FormEntryDto,
  FormDto,
  PageDto,
  getFirstName,
  getLastName,
  isSuccess,
  PageType,
  castQuery,
  EventDto,
  FormType,
  PageScope,
  StatusFilter,
  buildFilterChoicesDictionary,
  castQueryArrays,
} from 'fourwaves-shared';
import { ParticipantCard, ParticipantElement, NoResults, FilterPagination } from 'fourwaves-shared/components';
import { debounce } from 'lodash-es';
import { NuxtError } from '@nuxt/types';
import { ParticipantFilterBar } from '~/components/participants';
import LegacyHeader from '~/components/layout/LegacyHeader.vue';

const EventsModule = namespace('events');

@Component({
  scrollToTop: true,
  components: {
    LegacyHeader,
    ParticipantCard,
    NoResults,
    FilterPagination,
    ParticipantFilterBar,
  },
  validate({ store }) {
    const page = store.state.events.pages.find(({ type }: PageDto) => type === PageType.Participants);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isParticipant'] && !store.getters['events/isOrganizer']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class ParticipantsIndexPage extends Vue {
  @EventsModule.State pages!: PageDto[];
  @EventsModule.State currentEvent!: EventDto;
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;

  formId = '';
  form: FormDto | null = null;
  page: PageDto | null = null;
  participants: ParticipantElement[] = [];
  isLoading = false;
  firstLoadCompleted = false;
  totalCount = 0;
  totalPages = 1;
  areFiltersCollapsed = false;

  public head() {
    return { title: this.page ? this.transl8(this.page.name) : '' };
  }

  get headerTitle(): string | null {
    if (!this.page) return null;

    return this.transl8(this.page.name);
  }

  get model(): GetFormEntriesModel {
    const {
      page = 1,
      searchTerm = '',
      isBookmarked = false,
      isOnline = false,
    } = castQuery<Partial<GetFormEntriesModel>>(this.$route.query, {
      numbers: ['page', 'pageSize'],
      booleans: ['isBookmarked', 'isOnline'],
    });
    const { filterChoices = [] } = castQueryArrays<{ filterChoices: string[] }>(this.$route.query, ['filterChoices']);

    const formFields = this.form?.formSections.flatMap(x => x.formFields) || [];
    const filterChoicesDict = buildFilterChoicesDictionary(filterChoices, formFields);

    return {
      page,
      searchTerm,
      filterChoices: filterChoicesDict,
      isBookmarked,
      isOnline,
      formId: this.formId,
      pageSize: 20,
      statusFilters: [StatusFilter.Registered],
      sortField: this.form ? this.form.lastNameFormFieldId! : '',
      sortDirection: 'ascending',
    };
  }

  @Watch('model', { deep: true })
  async onModelChanged() {
    this.isLoading = true;
    await this.fetchData();
    if (this.firstLoadCompleted) this.$scrollTo('body');
  }

  created() {
    this.fetchData = debounce(this.fetchData, 250) as () => Promise<void>;
    this.form = this.currentEvent.forms.find(x => x.type === FormType.Registration)!;
    this.formId = this.form.id;
    this.page = this.pages.find(({ type }) => type === PageType.Participants) || null;
  }

  public async fetchData() {
    if (!this.formId) {
      this.isLoading = false;
      return;
    }

    const { items, totalCount, totalPages } = await this.$api.getFormEntries({ ...this.model });
    this.participants = items.map(participant => this.buildParticipantElement(participant));
    this.totalCount = totalCount;
    this.totalPages = totalPages;
    this.isLoading = false;
    if (!this.firstLoadCompleted) this.firstLoadCompleted = true;
  }

  public buildParticipantElement(participant: FormEntryDto): ParticipantElement {
    const firstName = getFirstName(participant, this.form!);
    const lastName = getLastName(participant, this.form!);

    return {
      firstName,
      lastName,
      id: participant.id,
      userId: participant.userId,
      affiliation: participant.affiliation,
      isBookmarkedByCurrentUser: participant.isBookmarkedByCurrentUser,
    };
  }

  public async toggleBookmark(participant: ParticipantElement) {
    if (!this.$auth.loggedIn) return;

    const item = this.participants.find(({ id }) => id === participant.id);

    if (!item) return;

    const updateResult = participant.isBookmarkedByCurrentUser
      ? await this.$api.unbookmarkFormEntry(participant.id)
      : await this.$api.bookmarkFormEntry(participant.id);

    if (isSuccess(updateResult)) item.isBookmarkedByCurrentUser = !item?.isBookmarkedByCurrentUser;
  }
}
